import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import styled from 'styled-components';

import { inquiryEventsLoaded } from 'dashboard/apps/InquiryTemplateEditor/InquiryRunTree/utils';
import { isInquiryRunEvent } from 'dashboard/apps/InquiryTemplateEditor/utils/inquiry-run';
import InquiryRunEventTimeline from 'dashboard/components/ConnectedInquiryRunTimelineCard/InquiryRunEventTimeline';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { RootState } from 'dashboard/redux/store';
import { loadInquiryRunData } from 'dashboard/redux/thunk-actions/inquiry-run';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { EventName } from 'lib/constants/events';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useApiLoader from 'lib/hooks/useApiLoader';
import useInquiryRunPermissionMap from 'lib/hooks/useInquiryRunPermissionMap';

type EventTypeGroup =
  | 'inquiry-status'
  | 'inquiry-run'
  | 'verifications'
  | 'workflow-runs'
  | 'inquiry-session';
const EVENT_TYPE_GROUP_FILTER_MAP: Record<EventTypeGroup, EventName[]> = {
  ['inquiry-status']: [
    EventName.InquiryApproved,
    EventName.InquiryCompleted,
    EventName.InquiryCreated,
    EventName.InquiryDeclined,
    EventName.InquiryExpired,
    EventName.InquiryMarkedForReview,
  ],
  ['inquiry-run']: [EventName.InquiryTransitioned, EventName.InquiryRunConditionalRouted],
  ['inquiry-session']: [
    EventName.InquirySessionStarted,
    EventName.InquirySessionCanceled,
    EventName.InquirySessionExpired,
  ],
  ['verifications']: [
    EventName.VerificationCreated,
    EventName.VerificationSubmitted,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationFailed,
    EventName.VerificationCanceled,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  ['workflow-runs']: [
    EventName.WorkflowRunCreated,
    EventName.WorkflowRunErrored,
    EventName.WorkflowRunResumed,
  ],
};
const EVENT_TYPE_GROUP_FILTER_OPTIONS = [
  {
    label: 'Inquiry Status',
    value: 'inquiry-status',
  },
  {
    label: 'Inquiry Run',
    value: 'inquiry-run',
  },
  {
    label: 'Inquiry Session',
    value: 'inquiry-session',
  },
  {
    label: 'Verifications',
    value: 'verifications',
  },
  {
    label: 'Workflow Runs',
    value: 'workflow-runs',
  },
];

const InquiryRunTimelineCard = styled(Card)`
  min-height: 528px;
  max-height: 900px;
  overflow-y: auto;
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  padding: ${Spacing[5]};
  padding-bottom: ${Spacing[4]};
  background: ${({ theme }) => theme.color.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  z-index: 2;
`;

const ShowRunButton = styled(Button).attrs(() => ({ variant: 'basic', width: 'auto' }))`
  margin-left: auto;
  color: ${({ theme }) => theme.color.text.primary};
`;

interface ConnectedInquiryRunTimelineCardProps {
  currentEnvironment: Environment;
  inquiry: Inquiry;
  locale?: InquiryIso6391LanguageCode;
  modalButtonEnabled?: boolean;
  handleShowRunsModal?: () => void;
}

type InquiryRunTimelineProps = ConnectedInquiryRunTimelineCardProps & PropsFromRedux;

const ConnectedInquiryRunTimelineCard = ({
  currentEnvironment,
  events,
  actors,
  inquiry,
  inquiryTemplateVersion,
  locale = 'en-US',
  modalButtonEnabled,
  stepsPreview,
  handleShowRunsModal,
  ...props
}: InquiryRunTimelineProps) => {
  const [selectedEventTypeGroups, setSelectedEventTypeGroups] = useState<EventTypeGroup[]>([]);
  const inquiryRunPermissionMap = useInquiryRunPermissionMap();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const versionId = inquiry.relationships['inquiry-template-version'].data.id;

  const { loading } = useApiLoader({
    loader: async () =>
      await props.loadInquiryRunData({
        inquiryId: inquiry.id,
        inquiryTemplateVersionId: versionId,
        locale,
        environmentId: currentEnvironment.id,
        inquiryRunPermissionMap,
      }),
    deps: [],
    skip: inquiryTemplateVersion?.id === versionId && inquiryEventsLoaded(events, inquiry.id),
    onError: () => null,
    onNetworkError: () => null,
  });

  const eventTypeFilterLabelTextMapper = useLabelTextMapper(
    EVENT_TYPE_GROUP_FILTER_OPTIONS,
    'event types',
    { emptyIsAll: true },
  );

  const showInquiryRunModalButton =
    modalButtonEnabled &&
    Object.values(inquiryRunPermissionMap).every((permissionEnabled) => permissionEnabled);
  !loading && events.filter(isInquiryRunEvent).length > 0;

  const { users, workflows, workflowRuns } = actors;
  const eventsToDisplay = useMemo(() => {
    if (selectedEventTypeGroups.length === 0) {
      return events;
    }
    const selectedEventTypes = selectedEventTypeGroups.flatMap(
      (eventTypeGroup) => EVENT_TYPE_GROUP_FILTER_MAP[eventTypeGroup],
    );
    return events.filter((event) => selectedEventTypes.includes(event.attributes.name));
  }, [events, selectedEventTypeGroups]);

  return (
    <InquiryRunTimelineCard>
      <Header>
        <Card.Title>
          Event timeline ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
          {showInquiryRunModalButton && handleShowRunsModal && (
            <ShowRunButton
              id='inquiry-details__show-run-modal-button'
              onClick={handleShowRunsModal}
            >
              View run
            </ShowRunButton>
          )}
        </Card.Title>
        <Spacer height={1} />
        <MultiSelect
          value={selectedEventTypeGroups}
          selectOptionValues={EVENT_TYPE_GROUP_FILTER_OPTIONS}
          labelTextMapper={eventTypeFilterLabelTextMapper}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onChange={(eventTypeGroups: EventTypeGroup[]) =>
            setSelectedEventTypeGroups(eventTypeGroups)
          }
          size='md'
        />
        <Spacer height={0.5} />
      </Header>
      <Card.Body>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <InquiryRunEventTimeline
            inquiry={inquiry}
            events={eventsToDisplay}
            users={users}
            workflows={workflows}
            workflowRuns={workflowRuns}
            inquiryTemplateVersion={inquiryTemplateVersion}
            stepsPreview={stepsPreview}
          />
        )}
      </Card.Body>
    </InquiryRunTimelineCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  inquiryTemplateVersion: state.inquiryRun.inquiryTemplateVersion,
  stepsPreview: state.inquiryRun.stepsPreview,
  events: state.inquiryRun.inquiryRunEvents,
  actors: state.inquiryRun.inquiryRunActors,
});

const mapDispatchToProps = {
  loadInquiryRunData,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedInquiryRunTimelineCard);
